import Papa from 'papaparse';
import * as XLSX from 'xlsx';

class FileImportUtils {
  /**
   * Validate if the file is a supported import type
   * @param {File} file - File to validate
   * @returns {boolean} Whether the file is valid
   */
  static validateFileType(file) {
    // Expanded list of valid file extensions and MIME types
    const validExtensions = [
      '.csv',
      '.xlsx',
      '.xls',
      '.txt',
      '.ods'  // OpenDocument Spreadsheet
    ];

    const validMimeTypes = [
      'text/csv',
      'application/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.oasis.opendocument.spreadsheet',
      'text/plain',
      'application/octet-stream'
    ];

    const fileName = file.name.toLowerCase();
    const hasValidExtension = validExtensions.some(ext => fileName.endsWith(ext));
    const hasValidMimeType = validMimeTypes.includes(file.type) || file.type === '';

    // Additional size check (100MB limit)
    const isValidSize = file.size <= 100 * 1024 * 1024;

    return hasValidExtension && hasValidMimeType && isValidSize;
  }

  /**
   * Parse CSV file
   * @param {File} file - File to parse
   * @returns {Promise} Promise resolving with parsed data
   */
  static parseCSV(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (event) => {
        const csvText = event.target.result;

        Papa.parse(csvText, {
          header: true,
          skipEmptyLines: true,
          dynamicTyping: true,
          complete: (results) => {
            if (results.data && results.data.length > 0) {
              resolve({
                data: results.data,
                columns: results.meta.fields || []
              });
            } else {
              reject(new Error('The CSV file appears to be empty or invalid'));
            }
          },
          error: (error) => {
            reject(new Error(`Error parsing CSV: ${error.message}`));
          }
        });
      };

      reader.onerror = () => {
        reject(new Error('Error reading the file'));
      };

      reader.readAsText(file);
    });
  }

  /**
   * Parse Excel file
   * @param {File} file - File to parse
   * @returns {Promise} Promise resolving with parsed data
   */
  static parseExcel(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (event) => {
        try {
          // Use array buffer instead of binary string for better compatibility
          const arrayBuffer = event.target.result;
          const workbook = XLSX.read(arrayBuffer, {
            type: 'array',
            cellDates: true,
            cellNF: true,
            cellFormulas: false,
            dateNF: 'yyyy-mm-dd'
          });

          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];

          // Convert to JSON with headers option
          const data = XLSX.utils.sheet_to_json(worksheet, {
            header: 1,
            defval: '',  // Default empty cells to empty string
            blankrows: false // Skip completely empty rows
          });

          // Only process if we have data
          if (data && data.length > 0) {
            // Separate headers from data rows
            const headers = data[0].map(h =>
              h !== undefined && h !== null ? h.toString().trim() :
                `Column${Math.random().toString(36).substring(2, 7)}`
            );

            // Map data rows to objects using headers
            const rows = data.slice(1)
              .filter(row => row.some(cell => cell !== undefined && cell !== null && cell !== '')) // Skip empty rows
              .map(row => {
                const obj = {};
                headers.forEach((header, i) => {
                  // Handle the value if it exists or use empty string
                  obj[header] = (i < row.length && row[i] !== undefined && row[i] !== null) ? row[i] : '';
                });
                return obj;
              });

            resolve({
              data: rows,
              columns: headers
            });
          } else {
            reject(new Error('The Excel file appears to be empty or has no data rows'));
          }
        } catch (error) {
          console.error("Excel parsing error:", error);
          reject(new Error(`Error parsing Excel file: ${error.message}`));
        }
      };

      reader.onerror = () => {
        reject(new Error('Error reading the file'));
      };

      // Use ArrayBuffer instead of BinaryString
      reader.readAsArrayBuffer(file);
    });
  }

  /**
   * Determine file type and parse accordingly
   * @param {File} file - File to parse
   * @returns {Promise} Promise resolving with parsed data
   */
  static async importFile(file) {
    // Validate file first
    if (!this.validateFileType(file)) {
      throw new Error(`File "${file.name}" is not a supported file type or exceeds size limit.`);
    }

    // Try to determine file type by extension
    const fileExt = file.name.toLowerCase().split('.').pop();

    // Use content type as fallback
    const contentType = file.type;

    // CSV-like formats
    if (fileExt === 'csv' || contentType.includes('csv') || contentType.includes('text/plain')) {
      return await this.parseCSV(file);
    }
    // Excel-like formats
    else if (['xlsx', 'xls', 'ods'].includes(fileExt) ||
      contentType.includes('excel') ||
      contentType.includes('spreadsheet') ||
      contentType.includes('octet-stream')) {
      return await this.parseExcel(file);
    }
    // Default to Excel parsing for unknown types as a last resort
    else {
      console.warn('Unknown file type, attempting Excel parsing:', file.type);
      return await this.parseExcel(file);
    }
  }

  /**
   * Generate a CSV template
   * @param {Array} templateData - Data to use for template
   * @param {string} filename - Name of the file to download
   * @param {string} [delimiter=','] - CSV delimiter
   */
  static downloadTemplate(
    templateData,
    filename = 'import_template.csv',
    delimiter = ','
  ) {
    const csv = Papa.unparse(templateData, { delimiter });
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
  }

  /**
   * Validate and clean data based on provided field mappings
   * @param {Array} data - Raw data to validate
   * @param {Object} fieldMappings - Mapping of fields with validation rules
   * @returns {Object} Validated and cleaned data with errors
   */
  static validateData(data, fieldMappings) {
    const validatedData = [];
    const errors = [];

    // Identify required fields
    const requiredFields = Object.entries(fieldMappings)
      .filter(([, config]) => config.required)
      .map(([key]) => key);

    // Check if required fields are mapped
    const missingRequired = requiredFields.filter(
      field => !Object.values(fieldMappings).some(mapping => mapping.key === field)
    );

    if (missingRequired.length > 0) {
      const missingLabels = missingRequired.map(field => fieldMappings[field].label);
      errors.push(`Missing required field mappings: ${missingLabels.join(', ')}`);
    }

    // Validate each row
    data.forEach((row, index) => {
      const validatedRow = {};
      let rowHasError = false;

      Object.entries(fieldMappings).forEach(([csvColumn, fieldConfig]) => {
        const value = row[csvColumn];

        if (fieldConfig.required && (!value || value.toString().trim() === '')) {
          errors.push(`Row ${index + 1}: Missing required value for ${fieldConfig.label}`);
          rowHasError = true;
        }

        if (fieldConfig.validate && value && !fieldConfig.validate(value)) {
          errors.push(`Row ${index + 1}: Invalid ${fieldConfig.label} format - ${value}`);
          rowHasError = true;
        }

        validatedRow[fieldConfig.key] = value;
      });

      if (!rowHasError) {
        validatedData.push(validatedRow);
      }
    });

    return { validatedData, errors };
  }
}

export default FileImportUtils;